export const uniqueKey = (prefix: string) =>
  `${prefix}_${
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}_${Math.random().toString(36).slice(2)}`
  }`;

export function limitText(value?: string | null, max = 300): string {
  if (!value) return "";
  const clean = String(value).replace(/\s+/g, " ").trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}

export const buildComment = (obj: Record<string, unknown>, max = 1900) => {
  const json = JSON.stringify(obj);
  return json.length > max ? `${json.slice(0, max)}…` : json;
};

export function extractMainErrorMessage(message?: string | null): string {
  if (!message) return "Unknown error";

  const clean = String(message).replace(/\s+/g, " ").trim();

  // Keep the short DOM / React / browser error headline only
  // Example:
  // Failed to execute 'insertBefore' on 'Node': The node before which...
  const domMatch = clean.match(/^Failed to execute '([^']+)' on '([^']+)':/i);
  if (domMatch) {
    return `${domMatch[2]}.${domMatch[1]}`;
    // => Node.insertBefore
  }

  const colonParts = clean.split(":");
  if (colonParts.length > 1) {
    return colonParts.slice(0, 2).join(":").trim();
  }

  return clean;
}

export function extractUsefulComponentStack(
  componentStack?: string | null,
): string {
  if (!componentStack) return "";

  const lines = componentStack
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  // remove bundle urls/noisy frames, keep readable component/html tags
  const useful = lines.filter((line) => {
    if (/https?:\/\//i.test(line)) return false;
    if (/assets\/entry\//i.test(line)) return false;
    return /^at\s+/i.test(line);
  });

  const preferred = useful.filter(
    (line) =>
      /at\s+(button|div|span|form|input|dialog|table|tbody|tr|td)\b/i.test(
        line,
      ) || /at\s+[A-Z][A-Za-z0-9_]+/i.test(line),
  );

  return limitText(
    (preferred.length ? preferred : useful).slice(0, 6).join(" > "),
    300,
  );
}

export function extractStackHeadline(stack?: string | null): string {
  if (!stack) return "";

  const firstRelevantLine = String(stack)
    .split("\n")
    .map((line) => line.trim())
    .find((line) => line && !/^at\s/.test(line));

  return limitText(firstRelevantLine || "", 220);
}

export function buildReactCrashComment(params: {
  message: string;
  stack?: string;
  componentStack?: string | null;
}) {
  // Repeated React/MUI frames can fill the report before an application frame.
  const frames = Array.from(
    new Set(
      (params.componentStack || "")
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean),
    ),
  );
  const components = frames.join(" > ");
  const componentSummary =
    components.length <= 600
      ? components
      : `${limitText(frames.slice(0, 3).join(" > "), 300)} … ${limitText(
          frames.slice(-3).join(" > "),
          290,
        )}`;
  // Keep frame URLs and line/column numbers: these identify the failing code
  // in production bundles. Put diagnostics before optional browser metadata.
  return limitText(
    [
      "source: AppErrorBoundary",
      `message: ${limitText(params.message, 250)}`,
      `release: ${limitText(import.meta.env.VITE_APP_VERSION || "local", 80)}`,
      `path: ${limitText(
        window.location.pathname +
          window.location.search +
          window.location.hash,
        180,
      )}`,
      params.stack ? `stack: ${limitText(params.stack, 500)}` : "",
      componentSummary ? `component: ${componentSummary}` : "",
      `ts: ${new Date().toISOString()}`,
      `locale: ${limitText(navigator.language, 30)}`,
      `ua: ${limitText(navigator.userAgent, 180)}`,
    ]
      .filter(Boolean)
      .join("\n"),
    2000,
  );
}

export function buildCrashSummary(params: {
  message?: string | null;
  stack?: string | null;
  componentStack?: string | null;
  url?: string | null;
  userAgent?: string | null;
}) {
  const shortMessage = extractMainErrorMessage(params.message);
  const stackHeadline = extractStackHeadline(params.stack);
  const shortComponent = extractUsefulComponentStack(params.componentStack);

  return [
    `message: ${shortMessage}`,
    stackHeadline ? `stack: ${stackHeadline}` : "",
    shortComponent ? `component: ${shortComponent}` : "",
    params.url ? `url: ${params.url}` : "",
    params.userAgent ? `ua: ${limitText(params.userAgent, 220)}` : "",
  ]
    .filter(Boolean)
    .join("\n");
}
